import {
  CommonModule,
  isPlatformBrowser,
  isPlatformServer,
} from '@angular/common';
import {
  Component,
  OnInit,
  WritableSignal,
  inject,
  isDevMode,
  signal,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationExtras, Router } from '@angular/router';
import { CommandExecutor } from '@app/action/command-executor';
import { HandleDataResponse } from '@app/action/response-handler';
import {
  AnalyticsService,
  CookieService
} from '@app/core';
import { AbstractRequestDto } from '@app/core/dto/abstract-request.dto';
import { ActionCommandResponseDto } from '@app/core/dto/action-command-response.dto';
import { RequestDataDto } from '@app/core/dto/request-data.dto';
import { SessionDto } from '@app/core/dto/session.dto';
import { UserContext } from '@app/core/dto/user-context-cart.dto';
import { FirebaseService } from '@app/core/services/firebase/firebase.service';
import { FontLoaderService } from '@app/core/services/font/font-loader.service';
import { BaseWidget } from '@app/widgets/base-widget/base-widget';
import { ProgressBarComponent } from '@app/widgets/progress-bar/progress-bar.component';
import { InputValidators } from '@app/widgets/utils/input-validation.util';
import { ModuleType } from '../../core/enums/module-type.enum';
import { PageType } from '../../core/enums/page-type.enum';
import { RequestType } from '../../core/enums/request-type.enum';
import { SessionStorageService } from '../../core/services/session/session-storage.service';
import { WidgetsService } from '../../core/services/widgets/widgets.service';
import { ActionEnum, NavigationTypeEnum } from '@app/core/dto/abstract-action.dto';
import { ModalType, PostMessageTypeEnum } from '@app/core/enums/modal.enums';
import { PopupPositionEnum } from '@app/core/enums/popup-view-type.enum';

@Component({
  selector: 'render-engine',
  templateUrl: './render-engine.component.html',
  styleUrl: './render-engine.component.scss',
  standalone: true,
  imports: [CommonModule, ProgressBarComponent],
  providers: [CookieService, InputValidators],
})
export class RenderEngineComponent
  extends BaseWidget
  implements OnInit {
  public apiBodyData: WritableSignal<AbstractRequestDto> = signal(
    new AbstractRequestDto(),
  );

  public slots: any = signal([]);
  public footerSlots: WritableSignal<any[]> = signal([]);
  public headerSlots: WritableSignal<any[]> = signal([]);
  suggestions: WritableSignal<any[]> = signal([]);
  allActionData: WritableSignal<any> = signal(null);
  public readonly module: WritableSignal<ModuleType> = signal(
    ModuleType.CATALOG,
  );
  viewContainer: WritableSignal<any> = signal(null);
  notFoundComponent: WritableSignal<any> = signal(null);
  public readonly footerComponent: WritableSignal<any> = signal(null);
  public isLoading: WritableSignal<boolean> = signal(false);
  public userContext: WritableSignal<UserContext> = signal({});
  public userSession: WritableSignal<SessionDto> = signal(null);
  public readonly sessionStorageService = inject(SessionStorageService);
  public readonly widgetsService = inject(WidgetsService);
  public readonly snackBar = inject(MatSnackBar);
  public readonly router = inject(Router);

  public readonly firebaseService = inject(FirebaseService);
  public readonly fontLoaderService = inject(FontLoaderService);
  public readonly analyticsService: AnalyticsService =
    inject(AnalyticsService);
  public apiInitTime: number;

  dynamicSlots = signal(new Map<string, any>());
  slotsLoaded = signal(false);
  private handleDataResponse: HandleDataResponse;

  constructor() {
    super();
    this.handleDataResponse = new HandleDataResponse(this);
    if (isPlatformServer(this.platformId)) {
      this.dyis.init();
      this.loadComponents();
    }
    this._bindViewContainer();
    this._bindFooter();
    this._init();
    this._loadLocalHeaderUI();
  }
  ngOnInit() { }

  public async fetchData(finalData?: any, requestType?: RequestType, module?: ModuleType): Promise<void> {
    this.isLoading.set(true);

    try {
      const res = await this._getDataAfterAction(finalData, requestType, module);

      // Handle response data or errors directly
      if (res?.error?.message) {
        this.snackBar.open(res.error.message, null, { duration: 1500 });
      } else {
        this.handleDataResponse.handleResponse(res,res?.RESPONSE, res?.SESSION);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  private async _init(): Promise<void> {
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };
    if (isPlatformBrowser(this.platformId)) {
      this._postMessage({type: PostMessageTypeEnum.PAGE_FETCH, action:{url: this.router.url} });
    }

    let pageType: any = this.activatedRoute.snapshot.data['pageType'];
    this.module.set(this.activatedRoute.snapshot.data['module']);

    this.apiBodyData.update((d) => ({ ...d, pageType: pageType }));

    // if(pageType === PageType.LISTING_PAGE && !this.hasQueryParams){
    //   this.removeStaticContent();
    // }

    if (
      [
        PageType.CATEGORY0_PAGE,
        PageType.LISTING_PAGE,
        PageType.PDP_PAGE,
        PageType.FAMILY_PAGE,
      ].includes(pageType)
    ) {
      this.module.set(ModuleType.CATALOG);
    } else if (pageType === PageType.CART) {
      this.module.set(ModuleType.CHECKOUT);
    } else if (pageType === PageType.USER) {
      this.module.set(ModuleType.USER);
    } else if (pageType === PageType.NPS) {
      this.module.set(ModuleType.NPS)
    }

    this.apiBodyData.update((d) => ({ ...d, pageUri: this.router.url }));

    if (!this.notFoundComponent()) {
      this._getDataInitially();
    }
  }

  private _loadLocalHeaderUI(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (this.activatedRoute.snapshot.data['module'] != ModuleType.USER) {
        this.headerSlots.set(
          this.sessionStorageService.get('header')?.slots ?? [],
        );
        this.footerSlots.set(
          this.sessionStorageService.get('footer')?.slots ?? [],
        );
      }
    }
  }

  public async loadComponents(): Promise<void> {
    const t = new Date().getTime();
    for (const [key, slot] of this.dyis.slots) {
      let w = slot;
      if (slot.lazy) {
        w = await slot.component;
      } else {
        w = slot.component;
      }
      this.dynamicSlots.update((ds) => {
        return ds.set(key, w);
      });
    }
    this.slotsLoaded.set(true);
    if (isDevMode()) {
      console.log('time elapsed server:', new Date().getTime() - t);
    }
  }

  private async _bindViewContainer(): Promise<void> {
    const c = await import(
      '@app/layouts/view-container/view-container.component'
    ).then((i) => i.ViewContainerComponent);
    this.viewContainer.set(c);
  }

  private async _bindFooter(): Promise<void> {
    const footerComponent = await import(
      '@app/widgets/footer/footer.component'
    ).then((i) => i.FooterComponent);
    this.footerComponent.set(footerComponent);
  }

  private _getDataInitially(data = null): void {
    this._getData(data || this.apiBodyData());
  }

  private _getData(data: AbstractRequestDto): void {
    this.apiInitTime = new Date().getTime();
    this.apiService.getPagefetchData(this.module(), data).subscribe({
      next: (res: any) => {
        this.handleDataResponse.handleResponse(res,res?.RESPONSE, res?.SESSION)
      },
      error: async (err) => {
        console.log(err);
      },
      complete: () => {
        this.isLoading.set(false);
      },
    });
  }

  private async _getDataAfterAction(data: any = null, requestType?: RequestType, module?: ModuleType): Promise<any> {
    this.isLoading.set(true);

    try {
      let response;
      if (requestType === RequestType.ACTION) {
        response = await this.apiService.getActionData(module, data || this.apiBodyData()).toPromise();
        this._postMessage({type: PostMessageTypeEnum.ACTION, action: response})
      } else if (requestType === RequestType.PAGE_FETCH) {
        response = await this.apiService.getPagefetchData(module, data || this.apiBodyData()).toPromise();
      }

      return response;
    } catch (error) {
      console.error("Error fetching data:", error);
      return error;
    } finally {
      this.isLoading.set(false);
    }
  }

  getRequiredDataforLogical(id: number): any {
    return this.slots().filter((slot: any) => slot.parentId === id);
  }

  getRequiredDataforHeaderLogical(id: number): any {
    return this.headerSlots().filter((slot) => slot.parentId === id);
  }

  actionInput = this.onActionCall.bind(this);
  public async onActionCall({ actionData }: any): Promise<void> {
    let { pageUri, pageType } = this.apiBodyData();
    let finalData: AbstractRequestDto = new AbstractRequestDto();

    finalData.pageUri = pageUri;
    finalData.pageType = pageType;

    const updatedAction: any = {};
    let requestType: RequestType = RequestType.ACTION;
    let module: ModuleType = this.module() ?? ModuleType.CATALOG;
    const queryParams = { ...this.activatedRoute.snapshot.queryParams };

    const requestData: RequestDataDto = new RequestDataDto({ finalData, updatedAction, pageUri, module, requestType, pageType, queryParams })
    if (actionData?.tracking) {
      this.analyticsService?.pushTrackingEvent(actionData.tracking);
    }

    if (!actionData?.type) {
      console.log('no action type detected');
      return;
    }
    const executor: CommandExecutor = new CommandExecutor(this);
    const result: ActionCommandResponseDto = await executor.execute(actionData, requestData);
    if (result) {
      if (result?.requestData?.updatedAction?.action === ActionEnum.EXTERNAL_NAVIGATION && result?.requestData?.updatedAction?.navigationType === NavigationTypeEnum.POPUP) {
        const dynamicPopupData = {
          data: {
            iframeInfo: result?.requestData,
          },
        };
        this.showPopup(dynamicPopupData, ModalType.IFRAME_POPUP, PopupPositionEnum.CENTER);
      } else {
        finalData = result?.requestData?.finalData
        requestType = result?.requestData?.requestType
        module = result?.requestData?.module
        result.requestData.finalData.pageUri = this.location.path();
        this.allActionData.set(result?.requestData?.finalData)
        if (result?.shouldFetchData) {
          this.fetchData(finalData, requestType, module);
        }
      }
    }
  }

  navigateTo(commands: any[], extras?: NavigationExtras) {
    this.router.navigate(commands, extras);
  }
}